<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { KeyRound, LogOut, Menu, Moon, Search, Sun, UserRound } from 'lucide-vue-next';
import { PERMISSIONS } from '@bsl/shared';
import { api } from '@/lib/http';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import AppSidebar from './AppSidebar.vue';
import NotificationBell from './NotificationBell.vue';
import UserAvatar from '@/components/ui/UserAvatar.vue';
import SearchPalette from './SearchPalette.vue';

const auth = useAuthStore();
const app = useAppStore();
const router = useRouter();
const menuOpen = ref(false);
const approvalsCount = ref(0);
const followupsCount = ref(0);
const searchOpen = ref(false);
const menuRoot = ref<HTMLElement | null>(null);
let timer: ReturnType<typeof setInterval> | undefined;

async function poll() {
  void app.refreshUnread();
  if (auth.can(PERMISSIONS.ACTIVITIES_LOG)) {
    api
      .get<{ overdue: number; today: number }>('/crm/follow-ups/summary')
      .then((s) => (followupsCount.value = s.overdue + s.today))
      .catch(() => undefined);
  }
  if (auth.can(PERMISSIONS.APPROVALS_VIEW)) {
    try {
      approvalsCount.value = (await api.get<{ total: number }>('/approvals/inbox', { params: { pageSize: 1 } })).total;
    } catch {
      /* non-critical */
    }
  }
}

async function logout() {
  menuOpen.value = false;
  await auth.logout();
  void router.push('/login');
}

function onKey(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    searchOpen.value = true;
  }
}

function onDoc(e: MouseEvent) {
  if (menuRoot.value && !menuRoot.value.contains(e.target as Node)) menuOpen.value = false;
}

onMounted(() => {
  void poll();
  void app.loadBranding();
  timer = setInterval(poll, 60_000);
  document.addEventListener('click', onDoc);
  window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  clearInterval(timer);
  document.removeEventListener('click', onDoc);
  window.removeEventListener('keydown', onKey);
});
</script>

<template>
  <div class="flex min-h-screen">
    <AppSidebar :approvals-count="approvalsCount" :followups-count="followupsCount" />
    <div class="flex min-w-0 flex-1 flex-col">
      <header class="surface sticky top-0 z-30 flex h-16 items-center gap-2 border-b px-4 sm:px-6">
        <button type="button" class="rounded-lg p-2 text-muted hover:bg-slate-100 lg:hidden dark:hover:bg-white/10" aria-label="Open menu" @click="app.mobileNavOpen = true">
          <Menu class="h-5 w-5" />
        </button>
        <button
          type="button"
          class="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-base px-3 py-1.5 text-left text-sm text-muted hover:border-navy-300 sm:max-w-md"
          aria-label="Search (Ctrl+K)"
          @click="searchOpen = true"
        >
          <Search class="h-4 w-4 shrink-0" />
          <span class="truncate">Search leads, customers, projects, sites…</span>
          <kbd class="ml-auto hidden rounded border border-base px-1.5 text-[10px] sm:inline">Ctrl K</kbd>
        </button>
        <div class="ml-auto flex items-center gap-1">
          <button
            type="button"
            class="rounded-lg p-2 text-muted hover:bg-slate-100 hover:text-current dark:hover:bg-white/10"
            :aria-label="app.theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
            @click="app.setTheme(app.theme === 'dark' ? 'light' : 'dark')"
          >
            <Sun v-if="app.theme === 'dark'" class="h-5 w-5" />
            <Moon v-else class="h-5 w-5" />
          </button>
          <NotificationBell />
          <div ref="menuRoot" class="relative ml-1">
            <button type="button" class="flex items-center gap-2 rounded-lg p-1 pr-2 hover:bg-slate-100 dark:hover:bg-white/10" :aria-expanded="menuOpen" aria-label="Account menu" @click="menuOpen = !menuOpen">
              <UserAvatar :name="auth.user?.fullName ?? ''" :file-id="auth.user?.avatarFileId" size="sm" />
              <span class="hidden max-w-[10rem] truncate text-sm font-medium md:block">{{ auth.user?.fullName }}</span>
            </button>
            <div v-if="menuOpen" class="card absolute right-0 z-40 mt-2 w-60 overflow-hidden py-1 shadow-pop" role="menu">
              <div class="border-b border-base px-4 py-2.5">
                <p class="truncate text-sm font-medium">{{ auth.user?.fullName }}</p>
                <p class="truncate text-xs text-muted">{{ auth.user?.email }}</p>
                <p class="mt-1 truncate text-[11px] text-muted">{{ auth.user?.roles.map((r) => r.name).join(', ') }}</p>
              </div>
              <RouterLink to="/profile" class="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-white/5" role="menuitem" @click="menuOpen = false">
                <UserRound class="h-4 w-4" /> My profile
              </RouterLink>
              <RouterLink to="/profile/security" class="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-white/5" role="menuitem" @click="menuOpen = false">
                <KeyRound class="h-4 w-4" /> Password & security
              </RouterLink>
              <button type="button" class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-slate-50 dark:hover:bg-white/5" role="menuitem" @click="logout">
                <LogOut class="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      </header>
      <main class="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6">
        <!-- Screens shared by several billing routes remount when the path changes so they never show stale data. -->
        <RouterView v-slot="{ Component, route: r }">
          <component :is="Component" :key="r.meta.kind ? r.path : undefined" />
        </RouterView>
      </main>
      <SearchPalette v-if="searchOpen" @close="searchOpen = false" />
    </div>
  </div>
</template>
